import type { ComponentChildren } from 'preact';
import './TopBar.css';
import { useEffect, useState } from 'preact/hooks';
import { chunkFailed } from '../../state/actions';
import { useConfig, useLayout, useTvOn } from '../../state/hooks';
import { SearchButton } from '../search/SearchButton';
import { Brand } from './Brand';
import { ExteriorButton } from './ExteriorButton';
import { OfficeTabs } from './OfficeTabs';
import { PollsLink } from './PollsLink';
import { ReplayLink } from './ReplayLink';
import { SimulationButton } from './SimulationButton';
import { SourceStamp } from './SourceStamp';
import { StatusIndicator } from './StatusIndicator';
import { TvButton } from './TvButton';

/**
 * Montagem por fase (§17.2 Fase 2b item 5): ExteriorButton (features.abroad) e TvButton (features.tv) existem atrás das
 * flags e só entram no cabeçalho nas Fases 10 e 11; o ShareButton, sem flag, só nasce na Fase 11.
 */
// CASCA: Fase 10 — ligar MOUNT.exterior (o ExteriorButton já obedece features.abroad, a Geography e a TV).
// CASCA: Fase 11 — ligar MOUNT.tv e montar o ShareButton entre ExteriorButton e TvButton (§6.1 bloco 2).
export const MOUNT = Object.freeze({ exterior: false, tv: false });

/** Classe do grupo de status: alvo do portal do TvExitButton, que o TvMode monta na TV (§12.1, FE §4.6). */
type ShareModule = typeof import('./ShareButton');
/** O módulo do Compartilhar, guardado depois do 1º `import()`. */
let shareModule: ShareModule | null = null;

/**
 * O botão Compartilhar **sob demanda** (ORD-F2-065): o botão, a imagem 1200×630 e os textos só são baixados com
 * `features.share` ligada. Desligado (o padrão), nada do Compartilhar entra no JS crítico do ao vivo nem no do replay
 * (que importa este arquivo pelo CSS do cabeçalho). Uma falha de rede deixa o cabeçalho sem o botão, sem erro.
 */
export function LazyShare() {
  const on = useConfig().features.share;
  const [mod, setMod] = useState<ShareModule | null>(shareModule);
  useEffect(() => {
    if (!on || mod) return;
    let alive = true;
    import('./ShareButton')
      .then((m) => {
        shareModule = m;
        if (alive) setMod(m);
      })
      .catch(() => chunkFailed());
    return () => {
      alive = false;
    };
  }, [on, mod]);
  return on && mod ? <mod.ShareButton /> : null;
}

export const TOP_BAR_STATUS_CLASS = 'top-bar__status';

/**
 * Cabeçalho (§6.1): `<header role="banner">` com `container: topbar`. Ordem: Brand · OfficeTabs · SearchButton · grupo de
 * status (StatusIndicator · SimulationButton · ReplayLink · ExteriorButton · ShareButton · TvButton). No celular as abas saem do
 * cabeçalho (barra própria do AppShell, `<OfficeTabs variant="bar">`). Na TV: Brand (só o nome) · abas inertes ·
 * StatusIndicator curto · SourceStamp `tv` · TvExitButton (portal). Sem dado próprio: os filhos leem o estado.
 */
export interface TopBarProps {
  /** A troca "Apuração · Linha do tempo" (ORD-F-026, PR 6), à direita das abas de cargo; o App a liga (app/screenSlot.tsx). */
  screenToggle?: ComponentChildren;
  /** ORD-F-153: o "Sair" (só com a porta e sessão aberta; o App o liga). Fora da TV. */
  account?: ComponentChildren;
}

export function TopBar({ screenToggle, account }: TopBarProps = {}) {
  const mode = useLayout().mode;
  const tv = useTvOn();
  const phone = mode === 'flow' || mode === 'tablet' || mode === 'land';
  return (
    // biome-ignore lint/a11y/noInteractiveElementToNoninteractiveRole: §6.1 bloco 7 e §15.4.2 pedem <header role="banner"> explícito
    <header class="top-bar" role="banner" data-testid="top-bar">
      <Brand />
      {!phone && <OfficeTabs variant="header" />}
      {!phone && !tv ? screenToggle : null}
      {!tv && <SearchButton />}
      <div class={TOP_BAR_STATUS_CLASS} data-testid="top-bar-status">
        <StatusIndicator />
        <SimulationButton />
        <ReplayLink />
        <PollsLink />
        {MOUNT.exterior && <ExteriorButton />}
        <LazyShare />
        {MOUNT.tv && <TvButton />}
        {tv ? null : account}
        {tv && <SourceStamp variant="tv" />}
      </div>
    </header>
  );
}
